import React, { useState } from "react";
import { useVariableValue } from "@devcycle/react-client-sdk";
import { isEqual, startCase } from "lodash";
import useDeepCompareEffect from "use-deep-compare-effect";

import { RouterTextLink } from "components/actions/RouterLink";
import { formatNumber } from "components/app_layout/Typography";
import SignalsContainer from "components/tags/SignalsContainer";
import { appRoutes } from "lib/appRoutes";
import { numberSort, stringSort } from "lib/columnSort";
import { EllipsisTooltipRouterLink } from "lib/core_components/EllipsisTooltip";
import { ColumnType, commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { IN_ROW_STAT, Table } from "lib/core_components/Table/Table";
import { RELEVANCY_SEARCH_NEW_USERS } from "lib/featureFlags";
import {
  FrameworkDto,
  UseSearchFrameworks,
  useSearchFrameworks,
} from "lib/hooks/api/frameworks/useSearchFrameworks";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import FrameworkIcon from "lib/icons/FrameworkIcon";
import VerifiedProviderIcon from "lib/icons/VerifiedProviderIcon";
import { EventData, EventDataTypes, EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { FrameworkStages } from "lib/types/graphQLEnums";
import { formatDate, formatDateOrOpenEnded, pluralise } from "lib/utils";
import { getSignalScoreTooltipText, SignalEntityType } from "lib/utils/signalUtils";
import { NewRelevanceScore } from "../../lib/core_components/NewRelevanceScore";
import { FrameworkCallOffTableCell } from "./FrameworkCallOffTableCell";
import FrameworkStage from "./FrameworkStage";
import {
  AWARD_TYPE_LABEL,
  convertFrameworkFiltersToRequest,
  FrameworkColumns,
  frameworkDetailsTarget,
  FrameworkFilters,
  getFrameworkColumnCopy,
  isValidSortField,
  PROCEDURE_TYPE_LABEL,
} from "./utils";

import css from "./FrameworkTable.module.scss";

function getSortOrder(key: string, sort?: SortState): "ascend" | "descend" | null {
  return sort?.field !== key ? null : sort.order === "ASC" ? "ascend" : "descend";
}

function getFrameworkColumns({
  filters,
  selectedColumns,
  logEvent,
  isRelevancySearchEnabled,
}: {
  filters: FrameworkFilters;
  selectedColumns: FrameworkColumns[];
  logEvent: (eventName: EventNames, data?: EventData) => void;
  isRelevancySearchEnabled: boolean;
}) {
  const sort = filters.sort;

  const signalScore: ColumnType<FrameworkDto> = {
    ...commonTableColumns.relevanceColumn,
    title: getFrameworkColumnCopy("signalScore"),
    key: "signalScore",
    dataIndex: "signalScore",
    render: (_, f) => (
      <NewRelevanceScore
        id={f.id}
        relevanceScore={f.signalScore ?? 0}
        entityType={SignalEntityType.Framework}
        eventAttributes={{
          "Framework GUID": f.id,
          Stage: f.stage,
          "Signal score": f.signalScore ?? 0,
          "Data type": EventDataTypes.frameworks,
        }}
      />
    ),
    sorter: numberSort((f: FrameworkDto) => f.signalScore ?? 0),
    sortOrder: getSortOrder("signalScore", sort),
    sortDirections: ["descend", "ascend", "descend"],
    showSorterTooltip: {
      title: getSignalScoreTooltipText("framework"),
    },
    sizeConfig: {
      small: 120,
      medium: 120,
      large: 120,
      xlarge: 120,
    },
  };

  const title: ColumnType<FrameworkDto> = {
    title: getFrameworkColumnCopy("title"),
    dataIndex: "title",
    key: "title",
    ellipsis: {
      showTitle: false,
    },
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: stringSort((f) => f.title),
    sortOrder: getSortOrder("title", sort),
    render: (_, f) => (
      <div className={css.frameworkProvider}>
        <FrameworkIcon size={32} className={css.frameworkIcon} />
        <div className={css.titleContainer}>
          <div className={css.frameworkName}>
            <EllipsisTooltipRouterLink
              fullText={f.title}
              linkProps={{
                ...frameworkDetailsTarget(f.id, filters),
                className: css.titleText,
                onClick: () => logEvent(EventNames.frameworkClicked, { "Framework ID": f.id }),
              }}
              linkText={f.title}
              tooltipProps={{ contentMaxWidth: 350 }}
            />
            {f.validatedByProvider && (
              <VerifiedProviderIcon
                className={css.verifiedProvider}
                ariaLabel="framework-verified-icon"
              />
            )}
          </div>
          {f.serviceProvider?.name && (
            <span className={css.providerText}>{f.serviceProvider?.name}</span>
          )}
        </div>
      </div>
    ),
    sizeConfig: {
      small: 200,
      medium: 300,
      large: 400,
      xlarge: 500,
    },
  };

  const procedureType: ColumnType<FrameworkDto> = {
    title: getFrameworkColumnCopy("procedureType"),
    dataIndex: "procedureType",
    key: "procedureType",
    ellipsis: {
      showTitle: false,
    },
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: stringSort((f) => f.procedureType),
    render: (_, framework: FrameworkDto) =>
      framework.procedureType ? PROCEDURE_TYPE_LABEL[framework.procedureType] : "--",
    sortOrder: getSortOrder("procedureType", sort),
  };

  const awardType: ColumnType<FrameworkDto> = {
    title: getFrameworkColumnCopy("awardType"),
    dataIndex: "awardType",
    key: "awardType",
    ellipsis: {
      showTitle: false,
    },
    sortDirections: ["ascend", "descend", "ascend"],
    render: (_, framework: FrameworkDto) =>
      framework.awardType ? AWARD_TYPE_LABEL[framework.awardType] : "--",
    sorter: stringSort((f) => f.awardType),
    sortOrder: getSortOrder("awardType", sort),
    sizeConfig: {
      small: 120,
      medium: 140,
      large: 140,
      xlarge: 250,
    },
  };

  const stage: ColumnType<FrameworkDto> = {
    title: getFrameworkColumnCopy("stage"),
    key: "stage",
    ellipsis: {
      showTitle: false,
    },
    dataIndex: "stage",
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: stringSort((f) => f.stage),
    sortOrder: getSortOrder("stage", sort),
    render: (_, framework: FrameworkDto) => <FrameworkStage stage={framework.stage} />,
    sizeConfig: {
      small: 125,
      medium: 140,
      large: 140,
      xlarge: 180,
    },
  };

  const value: ColumnType<FrameworkDto> = {
    ...commonTableColumns.valueColumn,
    key: "value",
    dataIndex: "value",
    title: getFrameworkColumnCopy("value"),
    ellipsis: {
      showTitle: false,
    },
    sortDirections: ["descend", "ascend", "descend"],
    sorter: numberSort((f) => f.value?.amount, getSortOrder("value", sort)),
    sortOrder: getSortOrder("value", sort),
    render: (_, framework: FrameworkDto) => {
      return (
        framework?.value &&
        formatNumber({
          value: framework.value?.amount,
          locale: "en-GB",
          currency: framework.value?.currency,
        })
      );
    },
  };

  const suppliers: ColumnType<FrameworkDto> = {
    key: "suppliers",
    dataIndex: "supplierCount",
    title: getFrameworkColumnCopy("suppliers"),
    sortDirections: ["descend", "ascend", "descend"],
    sorter: numberSort((f) => f.supplierCount, getSortOrder("suppliers", sort)),
    sortOrder: getSortOrder("suppliers", sort),

    // Setting this to stop the default clickthrough event if user doesn't exactly click suppliers link
    onCell: (_rec) => ({
      onClick: (event) => event.stopPropagation(),
    }),

    ...commonTableColumns.expandableColumn,
    render: (_, f) =>
      f.supplierCount > 0 ? (
        <>
          <RouterTextLink
            {...appRoutes.frameworkSuppliers(f.id)}
            eventName={EventNames.highlightClicked}
            eventAttributes={{
              "Highlight type": "Framework suppliers",
            }}
            className={IN_ROW_STAT}
            onClick={() => logEvent(EventNames.suppliersClicked, { "Framework ID": f.id })}
          >
            <div className={css.supplierCount}>{pluralise(f.supplierCount, "Supplier")}</div>
          </RouterTextLink>
          <div className={css.lotCount}>{pluralise(f.lotCount, "Lot")}</div>
        </>
      ) : (
        <span className={IN_ROW_STAT}>0</span>
      ),
    align: "left",
    sizeConfig: {
      small: 120,
      medium: 120,
      large: 120,
      xlarge: 180,
    },
  };

  const startDate: ColumnType<FrameworkDto> = {
    ...commonTableColumns.dateColumn,
    key: "startDate",
    title: getFrameworkColumnCopy("startDate"),
    ellipsis: {
      showTitle: false,
    },
    render: (_, f) =>
      f.contractPeriod?.startDate ? formatDate(new Date(f.contractPeriod.startDate)) : undefined,
    sorter: stringSort((f) => f.contractPeriod?.startDate),
    sortOrder: getSortOrder("startDate", sort),
    sortDirections: ["ascend", "descend", "ascend"],
    showSorterTooltip: false,
    sizeConfig: {
      small: 120,
      medium: 120,
      large: 120,
      xlarge: 180,
    },
  };

  const endDate: ColumnType<FrameworkDto> = {
    ...commonTableColumns.dateColumn,
    key: "endDate",
    title: getFrameworkColumnCopy("endDate"),
    ellipsis: {
      showTitle: false,
    },
    render: (_, f) => formatDateOrOpenEnded(f.contractPeriod?.endDate),
    sorter: stringSort((f) => f.contractPeriod?.endDate),
    sortDirections: ["ascend", "descend", "ascend"],
    sortOrder: getSortOrder("endDate", sort),
    showSorterTooltip: false,
    sizeConfig: {
      small: 120,
      medium: 120,
      large: 120,
      xlarge: 180,
    },
  };

  const closeDate: ColumnType<FrameworkDto> = {
    ...commonTableColumns.dateColumn,
    key: "closeDate",
    title: getFrameworkColumnCopy("closeDate"),
    ellipsis: {
      showTitle: false,
    },
    render: (_, f) => formatDateOrOpenEnded(f.tenderPeriod?.endDate),
    sorter: stringSort((f) => f.tenderPeriod?.endDate),
    sortDirections: ["ascend", "descend", "ascend"],
    sortOrder: getSortOrder("closeDate", sort),
    showSorterTooltip: false,
    sizeConfig: {
      small: 120,
      medium: 120,
      large: 120,
      xlarge: 180,
    },
  };

  const callOffs: ColumnType<FrameworkDto> = {
    key: "callOffs",
    dataIndex: "callOff",
    title: getFrameworkColumnCopy("callOffs"),
    sortDirections: ["descend", "ascend", "descend"],
    sorter: numberSort((f) => f.callOff.count, getSortOrder("value", sort)),
    sortOrder: getSortOrder("callOffs", sort),

    // Setting this to stop the default clickthrough event if user doesn't exactly click suppliers link
    onCell: (_rec) => ({
      onClick: (event) => event.stopPropagation(),
    }),

    ...commonTableColumns.expandableColumn,
    render: (_, f) => {
      const earlyStageFramework = [FrameworkStages.Upcoming, FrameworkStages.Tendering].includes(
        f.stage,
      );
      if (earlyStageFramework) {
        return <span className={IN_ROW_STAT}>N/A</span>;
      }

      const noCallOffs = f.callOff.count < 1;
      if (noCallOffs) {
        return <span className={IN_ROW_STAT}>--</span>;
      }

      return (
        <FrameworkCallOffTableCell
          averageAmount={f.callOff.averageAmount?.amount}
          callOffCount={f.callOff.count}
          currency={f.callOff.averageAmount?.currency ?? undefined}
          linkTo={`/frameworks/${f.id}/call-offs`}
          eventName={EventNames.highlightClicked}
          eventAttributes={{
            "Highlight type": "Framework call-offs",
          }}
          className={IN_ROW_STAT}
          onClick={() => logEvent(EventNames.callOffClicked, { "Framework ID": f.id })}
        />
      );
    },
    align: "left",
    sizeConfig: {
      small: 125,
      medium: 125,
      large: 150,
      xlarge: 200,
    },
  };

  const signals: ColumnType<FrameworkDto> = {
    title: getFrameworkColumnCopy("signals"),
    key: "signals",
    sizeConfig: {
      xlarge: 250,
      large: 150,
      medium: 150,
      small: 150,
    },
    showSorterTooltip: false,
    render: (_, f) => {
      return <SignalsContainer signals={f.signals || []} contextSource="In-row" />;
    },
  };

  const columns = {
    ...(!isRelevancySearchEnabled ? { signalScore } : {}),
    title,
    procedureType,
    awardType,
    stage,
    value,
    suppliers,
    startDate,
    endDate,
    closeDate,
    callOffs,
    signals,
  };

  return selectedColumns
    .map((c) => columns[c])
    .filter((c): c is ColumnType<FrameworkDto> => c !== undefined);
}

type SortState = Exclude<FrameworkFilters["sort"], undefined>;
type Props = {
  filters: FrameworkFilters;
  onSortChange: (sort: SortState) => void;
  selectedColumns: FrameworkColumns[];
  selectedRows?: string[];
  onSelectedRowsChange?: (selectedRowKeys: string[], selectedRows: FrameworkDto[]) => void;
  frameworkProvider?: UseSearchFrameworks;
};

export function FrameworkTable({
  filters,
  onSortChange,
  selectedColumns,
  selectedRows,
  onSelectedRowsChange,
  frameworkProvider = useSearchFrameworks,
}: Props) {
  const [pageSize, setPageSize] = useTablePageSize("framework-table");
  const [currentPage, setCurrentPage] = useState(1);
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const includeSignals = selectedColumns.includes("signals");
  const { isLoading, data, isError } = frameworkProvider(
    convertFrameworkFiltersToRequest(filters, { current: currentPage, pageSize }),
    includeSignals,
  );

  useDeepCompareEffect(() => {
    onSelectedRowsChange?.([], []);
    setCurrentPage(1);
  }, [filters]);

  const { logEvent } = useTracking();

  const columns = React.useMemo(() => {
    return getFrameworkColumns({
      filters,
      selectedColumns,
      logEvent,
      isRelevancySearchEnabled: isRelevancySearchEnabled,
    });
  }, [filters, selectedColumns, logEvent, isRelevancySearchEnabled]);

  return (
    <TrackingProvider
      data={{
        "Context source": "Table row",
      }}
    >
      <Table<FrameworkDto>
        ariaLabel="Frameworks Table"
        dataSource={data?.results || []}
        columns={columns}
        loading={isLoading}
        scroll={{ x: true }}
        pagination={{
          current: currentPage,
          pageSize,
          total: data?.totalResults || 0,
          onChange: (page, pageSize) => {
            setPageSize(pageSize);
            setCurrentPage(page);
            logEvent(EventNames.paginationSelected, { Value: page });
          },
          showSizeChanger: true,
        }}
        onChange={(_, __, sorter) => {
          if (Array.isArray(sorter)) {
            sorter = sorter[0];
          }
          // If the sort order is not defined, we don't want to trigger a sort without an order
          if (!filters.sort && !sorter.order) {
            return;
          }
          const sortField = isValidSortField(sorter.columnKey?.toString());
          if (sortField) {
            const newSortState: SortState = {
              order: sorter.order === "descend" ? "DESC" : "ASC",
              field: sortField,
            };
            if (!isEqual(filters.sort, newSortState)) {
              onSortChange?.(newSortState);
              // TODO: maybe move all tracking?
              logEvent(EventNames.tableSorted, {
                Context: "Column action",
                "Sorted by": startCase(newSortState.field),
                "Sort order": newSortState.order,
              });
            }
          }
        }}
        rowSelection={
          onSelectedRowsChange &&
          selectedRows && {
            onChange: (selectedRowKeys, selectedRows) => {
              onSelectedRowsChange(selectedRowKeys as string[], selectedRows);
            },
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedRows,
          }
        }
        rowKey="id"
        isError={isError}
      />
    </TrackingProvider>
  );
}
